import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import findResult from '@/utils/resultsHelper';
import { clientError, firstError, firstResponseError } from '@/utils/errors';
import { readStates, states } from '@/constants/lifecycle';
import { usersHavePermissions } from '@/users/helper';
import MemberPermissionsTable from '@/users/membership/components/MemberPermissionsTable';
import Dialog from '@/components/dialogs/Dialog';
import {
  hideShareCollection,
  fetchCollectionMembers,
  removeCollectionMember,
  updateCollectionMemberPermissions,
  shareCollection,
  updateShareCollectionProperties,
} from '../actions';
import CollectionMemberAddRow from './CollectionMemberAddRow';
import { collectionOwnerPermissions, collectionPermissionGroups, collectionPermissions } from '../constants';

const ShareCollectionDialog = ({
  allowAdd,
  allowRemove,
  allowSecure,
  category,
  code,
  fetchData,
  hide,
  isDirty,
  members,
  name,
  readStatus,
  removeMember,
  saveErrors,
  saveStatus,
  share,
  updatePermissions,
  updateProperties,
  theme,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => updateProperties({ saveErrors: newErrors });

  const titleText = () => ((allowAdd || allowRemove)
    ? intl.formatMessage({ id: 'collection.share.title' }, { name })
    : intl.formatMessage({ id: 'collection.security.title' }, { name }));

  const titleIcon = () => ((allowAdd || allowRemove) ? 'share alternate' : 'shield');

  const handleClose = () => {
    hide(category, code);
  };

  const validateForm = () => {
    const validationErrors = [];

    if (!usersHavePermissions(members, collectionOwnerPermissions)) {
      validationErrors.push(
        clientError(
          { entity: 'collection' },
          intl.formatMessage({ id: 'share.collection.validation.no.owners.title' }),
          intl.formatMessage({ id: 'share.collection.validation.owners.required' }),
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const handleDismiss = () => {
    setErrors([]);
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      share();
    }
  };

  return (
    <Dialog
      className="aridhia-collection-share-dialog smallwizard"
      error={firstResponseError(saveErrors)}
      icon={titleIcon()}
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={isDirty}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId="actions.apply"
      size="large"
      theme={theme}
      title={titleText()}
    >
      <div className="aridhia-user-permissions-dialog-container">
        <MemberPermissionsTable
          allowAdd={allowAdd}
          allowRemove={allowRemove}
          allowSecure={allowSecure}
          entity="collection"
          error={firstError(saveErrors)}
          fetchData={() => fetchData(category, code)}
          members={members}
          onErrorDismiss={handleDismiss}
          permissionGroups={collectionPermissionGroups}
          readStatus={readStatus}
          removeMember={(id) => removeMember(category, code, id)}
          updatePermissions={(id, value) => updatePermissions(category, code, id, value)}
        >
          {allowAdd && (<CollectionMemberAddRow category={category} code={code} allowSecure={allowSecure} />)}
        </MemberPermissionsTable>
      </div>
    </Dialog>
  );
};

const dialogProps = (sharingCollection) => (
  {
    category: sharingCollection.category,
    code: sharingCollection.code,
    saveStatus: sharingCollection.saveStatus,
    saveErrors: sharingCollection.saveErrors,
  }
);

const withoutItem = (sharingCollection) => (
  {
    ...dialogProps(sharingCollection),
    members: [],
    isDirty: false,
    ...readStates,
    allowAdd: false,
    allowRemove: false,
    allowSecure: false,
  }
);

const withItem = (item, sharingCollection, role) => {
  const {
    results, original, readStatus, readErrors,
  } = item.members;
  const isDirty = results !== original;

  return (
    {
      ...dialogProps(sharingCollection),
      members: results,
      name: item.name,
      isDirty,
      readStatus,
      readErrors,
      allowAdd: role.permissions.collections.share || item.permissions[collectionPermissions.INVITE_MEMBER],
      allowRemove: role.permissions.collections.share || item.permissions[collectionPermissions.REMOVE_MEMBER],
      allowSecure: role.permissions.collections.secure || item.permissions[collectionPermissions.SECURITY],
    }
  );
};

const mapStateToProps = ({
  auth: { role },
  collections: { collections, sharingCollection },
}) => {
  const item = findResult(collections, sharingCollection.category, sharingCollection.code);
  return item ? withItem(item, sharingCollection, role) : withoutItem(sharingCollection);
};

const mapDispatchToProps = {
  share: shareCollection,
  hide: hideShareCollection,
  fetchData: fetchCollectionMembers,
  removeMember: removeCollectionMember,
  updateProperties: updateShareCollectionProperties,
  updatePermissions: updateCollectionMemberPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareCollectionDialog);
